import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { IconButton } from '../Common/IconButton'
import { AnimatePresence, motion, type PanInfo } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { carouselTransitions, carouselVariants } from './CarouselVariants'
import { useCarouselRegistry, type CarouselAuditContext } from './CarouselRegistry'
import { useCarouselSequence } from './useCarouselSequence'

export type CinematicCarouselProps = {
  journeyId: string
  onComplete?: () => void
  onDismiss?: () => void
  /** Called when the registry/preload pipeline fails. Defaults to console.error.
   * The carousel is decorative — a fetch failure must never crash the host
   * app, so this never throws during render. */
  onError?: (error: Error) => void
  auditContext?: CarouselAuditContext
  className?: string
}

const swipeThreshold = 70
const swipeVelocity = 450

const CarouselStage = ({
  journeyId,
  onComplete,
  onDismiss,
  onError,
  auditContext,
  className,
}: CinematicCarouselProps) => {
  const { config, error } = useCarouselRegistry(journeyId, auditContext)
  const [dismissedJourneyId, setDismissedJourneyId] = useState<string | null>(null)
  const handleDismiss = useCallback(() => {
    setDismissedJourneyId(journeyId)
    onDismiss?.()
  }, [journeyId, onDismiss])

  // Report errors via callback (or default console.error) instead of throwing
  // during render. The previous `throw error` pattern required every consumer
  // to wrap the carousel in an ErrorBoundary or face an app crash on any
  // transient fetch hiccup.
  const reportedErrorRef = useRef<Error | null>(null)
  useEffect(() => {
    if (!error || reportedErrorRef.current === error) return
    reportedErrorRef.current = error
    if (onError) onError(error)
    else console.error('[CinematicCarousel] Registry load failed.', error)
  }, [error, onError])

  if (error || !config || dismissedJourneyId === journeyId) return null

  return (
    <ReadyCarousel
      config={config}
      onComplete={onComplete}
      onDismiss={handleDismiss}
      className={className}
    />
  )
}

const ReadyCarousel = ({
  config,
  onComplete,
  onDismiss,
  className,
}: {
  config: NonNullable<ReturnType<typeof useCarouselRegistry>['config']>
  onComplete?: () => void
  onDismiss?: () => void
  className?: string
}) => {
  const {
    activeSlide,
    direction,
    progress,
    next,
    previous,
    bindVideo,
  } = useCarouselSequence({ config, onComplete })
  const dismiss = config.settings.dismissible ? onDismiss : undefined

  const handleDragEnd = useCallback((_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (Math.abs(info.offset.x) < swipeThreshold && Math.abs(info.velocity.x) < swipeVelocity) return
    if (info.offset.x < 0) void next()
    else void previous()
  }, [next, previous])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        void next()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        void previous()
      } else if (event.key === 'Escape' && dismiss) {
        event.preventDefault()
        dismiss()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dismiss, next, previous])

  // activeSlide can be transiently undefined while useCarouselSequence
  // resets after an in-place config swap. Avoid the .id read crash.
  if (!activeSlide) return null

  const isFullscreen = config.settings.uiMode === 'fullscreen'
  const progressPosition = isFullscreen ? 'top-0' : 'bottom-0'
  const shellClass = isFullscreen
    ? 'fixed inset-0 h-dvh w-screen'
    : 'fixed left-1/2 top-1/2 h-[min(82dvh,820px)] w-[min(92vw,1200px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[var(--border)] shadow-[var(--shadow-pop)]'

  return (
    <motion.section
      // eslint-disable-next-line no-restricted-syntax -- full-screen cinematic journey shell with its own focus and Escape handling
      role="dialog"
      aria-modal="true"
      aria-label={`Cinematic journey ${config.journeyId}`}
      className={`${shellClass} z-[var(--z-cinematic)] isolate overflow-hidden bg-[var(--bg)] backdrop-blur-3xl ${className ?? ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    >
      {config.settings.showProgress && (
        <div className={`pointer-events-none absolute ${progressPosition} left-0 right-0 z-30 h-1 bg-[var(--surface-muted)]`}>
          <motion.div
            className="h-full origin-left bg-[var(--accent)]"
            style={{ scaleX: progress }}
          />
        </div>
      )}

      {dismiss && (
        <IconButton
          size="lg"
          variant="tertiary"
          label="Close cinematic journey"
          onClick={dismiss}
          className="absolute right-5 top-5 z-40"
          icon={<CloseRoundedIcon fontSize="small" />}
        />
      )}

      <div className="h-full w-full overflow-hidden [perspective:1400px]">
        <AnimatePresence initial={false} mode="popLayout" custom={{ direction }}>
          <motion.div
            key={activeSlide.id}
            custom={{ direction }}
            variants={carouselVariants[activeSlide.swipeEffect]}
            initial="enter"
            animate="center"
            exit="exit"
            transition={carouselTransitions[activeSlide.swipeEffect]}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.14}
            onDragEnd={handleDragEnd}
            className="absolute inset-0 cursor-grab touch-pan-y select-none overflow-hidden active:cursor-grabbing"
          >
            {activeSlide.type === 'video' ? (
              <video
                ref={bindVideo}
                src={activeSlide.assetUrl}
                muted
                playsInline
                autoPlay
                preload="auto"
                loop={activeSlide.loopVideo}
                draggable={false}
                className="pointer-events-none h-full w-full object-cover"
              />
            ) : (
              <img
                src={activeSlide.assetUrl}
                alt=""
                draggable={false}
                className="pointer-events-none h-full w-full object-cover"
              />
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[color-mix(in_srgb,var(--bg)_10%,transparent)] via-transparent to-[color-mix(in_srgb,var(--bg)_25%,transparent)]" />
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.section>
  )
}

export const CinematicCarousel = (props: CinematicCarouselProps) => {
  if (typeof document === 'undefined') return null
  return createPortal(
    <AnimatePresence>
      <CarouselStage {...props} />
    </AnimatePresence>,
    document.body,
  )
}
